import { z } from 'zod';

/**
 * THE ERP EXCHANGE, WHICH IS A FOLDER AND NOT AN API.
 *
 * BuildSmart/Candy does not offer an endpoint to call. The integration that
 * exists today is files: payroll receives a CLOCKCARDSBATCH workbook from us,
 * and hands us their own exports to read employee and contract master data
 * back out of. Both directions here are therefore paths on disk, watched and
 * written by scheduled jobs, not URLs.
 *
 * WHICH WAY EACH FACT TRAVELS, because the two are not symmetrical:
 *
 *   OUT   time and attendance ONLY — worked days, OT1, OT2 hours, per
 *         employee per contract per month. Hadir is the system of record for
 *         what a worker did.
 *
 *   IN    employee and contract master — payroll numbers, GL codes. The ERP is
 *         the system of record for who a worker IS and how they are paid, so
 *         an inbound value REPLACES what Hadir holds.
 *
 * Both halves default to OFF. A tenant with no ERP must not have jobs quietly
 * scanning folders that do not exist, and — more to the point — the inbound
 * half rewrites payroll routing for the whole workforce, which is not something
 * to inherit from a default.
 */

/**
 * `null` rather than `''` for an unset path: an empty string is a valid
 * relative path (the process's own working directory), and a job pointed at
 * the API's cwd would scan the source tree.
 */
const DirPath = z.string().trim().min(1).nullable().default(null);

export const ErpExportSettingsSchema = z.object({
  /**
   * The scheduled run. The export can always be taken by hand from the reports
   * screen; this only decides whether it ALSO happens on its own.
   */
  enabled: z.boolean().default(false),
  /**
   * Day of the month to export the month that just ended.
   *
   * Capped at 28 deliberately. A 29, 30 or 31 is a day that does not exist in
   * every month, so a job keyed to it would silently skip February — and a
   * payroll export that is simply absent for one month a year is exactly the
   * kind of failure nobody notices until the month it matters.
   */
  dayOfMonth: z.number().int().min(1).max(28).default(1),
  /*
   * NO `hourOfDay`. There was one, defaulting to 5, and no code path in the
   * repository ever read it — the export fires at the fixed 06:00 slot in
   * `jobs/schedule.ts`, after the 04:00 API sync and the 05:00 master import,
   * and that ordering is what stops a month of hours being posted against
   * yesterday's payroll routing. The setting existed, validated, saved and
   * decided nothing; removed rather than wired, because wiring it would have
   * handed an admin a way to break the ordering from a settings screen.
   */
  /** Where the workbook is written for the ERP to collect. */
  outboundDir: DirPath,
  /** Optional: also email the workbook to payroll. */
  emailTo: z.array(z.string().email()).default([]),
  /**
   * Refuse to export a month whose PayPeriod is still OPEN.
   *
   * Off by default because most installs do not run pay periods at all, and a
   * gate that silently produces nothing would be worse than a provisional file.
   * On, it means payroll never receives numbers that can still move.
   */
  requireClosedPeriod: z.boolean().default(false),
});

export const ErpImportSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  /** Folder the ERP drops its exports into. Scanned once a day. */
  inboundDir: DirPath,
  /**
   * Where a file goes once it has been read. A processed file MUST leave the
   * inbound folder or the next run reads it again; when this is unset the job
   * refuses to apply anything rather than risk re-applying a stale file every
   * night for a year.
   */
  archiveDir: DirPath,
});

/**
 * THE SAME EXCHANGE, OVER HTTP — per tenant, never in code.
 *
 * The header above was written when BuildSmart offered no endpoint. It does:
 * `/webapi` is a 130-operation OpenAPI surface (study:
 * docs/buildsmart-webapi-integration-study-2026-08-31.md). This block is where
 * ONE customer's instance is described.
 *
 * ── WHY EVERY DEFAULT HERE IS NULL OR EMPTY ─────────────────────────────────
 * Nothing about a specific customer may sit in a source file. A base copy of
 * Hadir with `192.168.1.89` or a code prefix of `LB` baked in is a base copy
 * that is quietly wrong for the next customer, and the fix has to be found and
 * rewritten in a file rather than typed into a settings screen once.
 *
 * So the defaults describe NO ONE. An unconfigured tenant has an ERP API that
 * is off and cannot accidentally point at somebody else's server.
 *
 * ── WHAT IS DELIBERATELY NOT HERE: THE CREDENTIALS ──────────────────────────
 * `userName` and `password` are read from the environment, not from this
 * block. Settings round-trip through an admin screen and are stored as a JSON
 * blob that is read by more code, logged more often and backed up more widely
 * than a secret should be. The connection is configuration; the credential is
 * a secret, and they do not belong in the same place.
 */
export const ErpApiSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  /** Which ERP. Named so a second vendor is a value, not a fork. */
  vendor: z.enum(['buildsmart']).default('buildsmart'),
  /** Server root INCLUDING the vendor's path prefix, e.g. `http://host/webapi`. */
  baseUrl: z.string().trim().url().nullable().default(null),
  /** The tenant's short name, from the ERP's own `GetEIDs`. */
  shortName: z.string().trim().min(1).nullable().default(null),
  /** Which organisation to read. From `GetOrgs`. */
  orgId: z.number().int().positive().nullable().default(null),
  /**
   * Which payrolls to read, and what each one is CALLED in the outbound batch.
   *
   * Two values because they are two different things: `payrollId` selects the
   * ERP token scope, while `payrollNumber` is the `PRL` code written onto the
   * employee — CHAR(10), leading zeros significant (`002`, never `2`).
   * Deriving one from the other silently mis-routes a whole payroll.
   */
  payrolls: z
    .array(
      z.object({
        payrollId: z.number().int().positive(),
        payrollNumber: z.string().trim().min(1),
        label: z.string().trim().default(''),
      })
    )
    .default([]),
  /**
   * Employee-code prefixes, per customer. Al Wathba uses `ST*` for staff and
   * `LB*` for site labour; the next customer will not.
   *
   * Used only to CROSS-CHECK Hadir's own `workforceType` — a code whose prefix
   * disagrees with the flag is reported and skipped, never silently resolved,
   * because one of the two is wrong about whether that person may log in.
   * Empty lists mean "no convention to check against", which is the safe
   * default: it skips the cross-check rather than inventing one.
   */
  codePrefixes: z
    .object({
      staff: z.array(z.string().trim().min(1)).default([]),
      worker: z.array(z.string().trim().min(1)).default([]),
    })
    .default(() => ({ staff: [], worker: [] })),
  /**
   * Contract-code prefixes that mean "this is a construction site".
   *
   * The ERP does NOT say what kind of place a contract is, and guessing was
   * wrong twice in production: leaving Hadir's OFFICE default filed 49 projects
   * as offices, and typing everything CONSTRUCTION filed a real-estate company
   * as a site. The customer knows — Al Wathba codes construction contracts `C…`
   * and everything else is an office — but that is their convention, not the
   * vendor's, so it is configured here rather than compiled in.
   *
   * Empty (the default) means "no convention": worksites are created on Hadir's
   * default type and REPORTED as needing classification. A base copy guesses at
   * nothing.
   */
  siteCodePrefixes: z.array(z.string().trim().min(1)).default([]),
  /**
   * BuildSmart organisation → Hadir `LegalEntity`.
   *
   * The ERP runs several companies on one server (Al Wathba has four), and a
   * payroll belongs to exactly one of them. Hadir models the same thing as
   * `LegalEntity`, which is what `Employee.legalEntityId` points at, so a
   * synced employee can carry the company they are actually employed by
   * instead of everyone landing in one undifferentiated workforce.
   *
   * ── WHY THE ENTITIES ARE NOT CREATED FROM THE ERP ───────────────────────
   * `LegalEntity` requires an EMIRATE, and a licence number and WPS
   * establishment id are what make it useful. BuildSmart's organisation record
   * carries none of them — and one organisation here runs payrolls in two
   * emirates, so it cannot even be inferred from the payroll names. Creating
   * entities from the ERP would mean inventing those fields, which is the
   * mistake this integration has already made three times.
   *
   * So an admin creates the entities, once, with the facts only they hold, and
   * this maps ERP org ids onto them. An unmapped organisation is REPORTED and
   * its employees are left without an entity rather than being assigned a
   * plausible wrong one.
   */
  orgLegalEntities: z
    .array(
      z.object({
        orgId: z.number().int().positive(),
        legalEntityId: z.string().trim().min(1),
        label: z.string().trim().default(''),
      })
    )
    .default([]),
  /**
   * How this instance spells its coded values, e.g. `{ "M": "MALE" }`.
   *
   * Configured rather than hard-coded because these are an ERP INSTANCE's
   * conventions, not the vendor's: the same BuildSmart build can be set up with
   * `1`/`2` or `MALE`/`FEMALE`. Anything unmapped is reported and left unset —
   * a silently defaulted enum is a wrong fact that looks like a right one.
   */
  valueMaps: z
    .object({
      gender: z.record(z.string()).default({}),
      maritalStatus: z.record(z.string()).default({}),
      /**
       * ERP status code -> Hadir `EmploymentStatus`, e.g.
       * `{ "01": "ACTIVE", "06": "RESIGNED", "04": "TERMINATED", "12": "ACTIVE" }`.
       *
       * The ERP is the authority on whether somebody still works here, and
       * without this Hadir never finds out: a synced employee takes the ACTIVE
       * default and nothing moves it, so a leaver stays in headcount, on
       * rosters and on the face-enrolment list indefinitely.
       *
       * Map the codes that STAY as well as the ones that go. `12 Transfer to
       * another branch` means the person changed payroll, not employer, so it
       * points at ACTIVE — leaving it out would report those people as unmapped
       * on every run until somebody silenced the noise by guessing.
       *
       * Empty by default, and an unmapped code changes nothing and is reported
       * with the ERP's own description. A default here would deactivate real
       * employees on an install nobody had configured.
       */
      employmentStatus: z.record(z.string()).default({}),
    })
    .default(() => ({ gender: {}, maritalStatus: {}, employmentStatus: {} })),
  /**
   * The ERP status codes that mean a payroll posting is STILL LIVE.
   *
   * One person can hold a posting in two payrolls, and which of them bills
   * their hours is a pay decision. The ERP answers it: at Al Wathba a live
   * posting reads `01 Active` and the one they left reads `12 Transfer to
   * another branch` with a termination date. But `01` and `12` are this
   * INSTANCE's codes, the way `M`/`F` above are, so they are configured beside
   * the other coded values instead of compiled in.
   *
   * A posting counts as closed only when its termination date has PASSED *and*
   * its status is not one of these. Both, because either alone is wrong in a
   * way that costs money: a transfer stamped with a future date is still live
   * and still owed the month, and a termination that was cancelled leaves a
   * past date on a posting the ERP still calls active.
   *
   * EMPTY (the default) means the codes are unknown, and then nothing is judged
   * closed at all: a person in two payrolls stays a refusal for somebody to
   * settle. A base copy guesses at nothing.
   */
  activeStatusCodes: z.array(z.string().trim().min(1)).default([]),
  /**
   * Pay points that mean "works across sites", not "works at this site".
   *
   * A pay point is a cost allocation, and most of them name a contract — which
   * is why `worksiteAssignmentSync` can read 23 of 28 as places. The rest are
   * the ones where the ERP is saying the opposite: at Al Wathba `BS` is the
   * Balance Sheet, carrying a driver who works across every site and punches
   * only at the camp, and staff who move between several sites in a day
   * (customer, 2026-09-08).
   *
   * Naming one here has two effects, and the second is the reason it exists:
   *
   *   1. NO worksite assignment is ever proposed from it. That already
   *      happened by accident — a balance-sheet code matches no worksite code —
   *      and an accident is not a rule. Now it is stated, and reported under
   *      its own heading instead of in the "no match" pile beside genuine
   *      spelling mismatches somebody should fix.
   *
   *   2. Anybody on it who still holds Hadir's strict `SELF_ASSIGNED` default
   *      is widened to `SELF_ANY_SITE`. Strict plus no assignment does not mean
   *      "restricted" — it means CANNOT PUNCH ANYWHERE, which is what 95 people
   *      at this customer were. See `roamingAccess.ts`.
   *
   * Empty by default. A base copy knows nothing about anybody's chart of
   * accounts, and guessing which code means "roams" would hand a whole pay
   * point the run of every site.
   */
  payPointRoaming: z.array(z.string().trim().min(1)).default([]),
  /**
   * Pay points MAPPED to a Hadir worksite whose code is spelled differently.
   *
   * The assignment pass matches a pay point to a worksite by exact code, which
   * works for the 23 pay points that are contract codes because both systems
   * spell those the same. It cannot work for the others, and the others are not
   * a rounding error: at Al Wathba `OH4` is Projects Management Dubai carrying
   * 138 people and `OH2` is the Dubai office carrying 48. Real places, different
   * codes in each system, and until they are mapped every one of those people
   * sits on no roster at all — their face is never matched and their day reads
   * blank.
   *
   * ── WHY A MAPPING AND NOT A CLEVERER MATCH ────────────────────────────────
   * Name similarity was considered and rejected: "Main Office - Dubai" against
   * a Hadir worksite called "Dubai Office" is a guess, and a wrong guess here
   * puts a person on a roster they never attend, where the day reads as absent.
   * A mapping is somebody stating the answer once. It then holds for every
   * person who ever lands on that pay point, which is the point — the ERP gains
   * and loses people every month and nobody should be assigning them by hand.
   *
   * Empty by default: a base copy knows nothing about anybody's cost centres.
   */
  payPointWorksites: z
    .array(
      z.object({
        payPoint: z.string().trim().min(1),
        worksiteCode: z.string().trim().min(1),
      })
    )
    .default([]),
  /**
   * Assign people to worksites from their pay point on the NIGHTLY run.
   *
   * Off by default, and deliberately a setting rather than a decision baked
   * into the job. Reading a cost allocation as a place is right often enough to
   * be worth doing and wrong often enough that no base copy should do it
   * unattended — that was the original reasoning and it still holds for a
   * customer who has looked at nothing.
   *
   * It stops holding once a customer HAS looked. After the mapping above is
   * filled in, an assignment is no longer an inference: it is either the two
   * systems agreeing on a code or a person having stated the answer. Leaving it
   * manual then means every new hire is invisible until somebody remembers to
   * run a command, which is a worse failure and a recurring one.
   */
  assignFromPayPoint: z.boolean().default(false),
  /**
   * ERP pay-component codes → the Hadir salary column each one feeds.
   *
   * The only readable route to a salary in this API. `GetEmployee` returns no
   * salary and no bank details — proven against 60 live records on 2026-09-08,
   * they exist only on the WRITE payload — but `GetPayrollValues` returns what
   * was PAID under each component, per period, and basic pay and the housing
   * and transport allowances are in there.
   *
   * Configured rather than compiled in because these are one instance's own
   * earnings codes. At Al Wathba basic reads `NTM` and the allowances `HRA`,
   * `TRA` and `GAL`; the next customer's will not. A code in no list is
   * REPORTED and contributes nothing — an allowance nobody mapped is the
   * difference between a right total and a plausible wrong one, and guessing
   * from a description would put a bonus in somebody's basic.
   */
  salaryComponents: z
    .object({
      basic: z.array(z.string().trim().min(1)).default([]),
      housing: z.array(z.string().trim().min(1)).default([]),
      transport: z.array(z.string().trim().min(1)).default([]),
      other: z.array(z.string().trim().min(1)).default([]),
    })
    .default(() => ({ basic: [], housing: [], transport: [], other: [] })),
});

export const ErpSettingsSchema = z.object({
  export: ErpExportSettingsSchema.default(() => ErpExportSettingsSchema.parse({})),
  import: ErpImportSettingsSchema.default(() => ErpImportSettingsSchema.parse({})),
  api: ErpApiSettingsSchema.default(() => ErpApiSettingsSchema.parse({})),
});

export type ErpSettings = z.infer<typeof ErpSettingsSchema>;
export type ErpApiSettings = z.infer<typeof ErpApiSettingsSchema>;
export type ErpExportSettings = z.infer<typeof ErpExportSettingsSchema>;
export type ErpImportSettings = z.infer<typeof ErpImportSettingsSchema>;
